import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { prepareSocioEconomic, socioEconomicColumns } from '../../services/dataService';
import { SocioEconomicData } from '../../types/domain';
import {
  SocioEconomicFields,
  emptySocioEconomic
} from '../components/SocioEconomicFields';
import {
  ControlPlaceSelect,
  ControlPlaceSelection,
  emptyControlPlaceSelection,
  resolveControlPlace
} from '../components/ControlPlaceSelect';

const CompleteProfile: React.FC = () => {
  const { user, refreshProfile } = useAuth();
  const [fullName, setFullName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [diabetesType, setDiabetesType] = useState('Tipe 1');
  const [controlPlace, setControlPlace] = useState<ControlPlaceSelection>(emptyControlPlaceSelection);
  const [socio, setSocio] = useState<SocioEconomicData>(emptySocioEconomic);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Turns the picker's choice into a control_places row before saving, so
      // the profile references a hospital by id rather than by free text.
      const place = await resolveControlPlace(controlPlace);

      // FR-03 is "data tetap" — filled once, here. It rides along in the same
      // upsert rather than a follow-up write, so a failure halfway cannot
      // leave a profile that exists but has no family data and no way back to
      // this form.
      const socioColumns = socioEconomicColumns(prepareSocioEconomic(socio));

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert([
          {
            id: user.id,
            full_name: fullName,
            birth_date: birthDate,
            diabetes_type: diabetesType,
            hospital_id: place.id,
            control_place: place.name,
            ...socioColumns,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ]);

      if (profileError) {
        throw profileError;
      }

      await refreshProfile();
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Gagal menyimpan profil.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen">
      <div className="screen-card">
        <div className="section-title">Lengkapi Profil</div>
        <div className="subtitle">
          Silakan lengkapi data diri Anda sebelum melanjutkan.
        </div>

        <form onSubmit={handleSave}>
          <div className="field-group">
            <div className="field">
              <label htmlFor="fullName">Nama Lengkap</label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="Nama Lengkap"
              />
            </div>

            <div className="field">
              <label htmlFor="birthDate">Tanggal Lahir</label>
              <input
                id="birthDate"
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label htmlFor="diabetesType">Tipe Diabetes</label>
              <select
                id="diabetesType"
                value={diabetesType}
                onChange={(e) => setDiabetesType(e.target.value)}
              >
                <option value="Tipe 1">Tipe 1</option>
                <option value="Tipe 2">Tipe 2</option>
                <option value="Monogenik">Monogenik</option>
                <option value="Lainnya">Lainnya</option>
              </select>
            </div>

            <ControlPlaceSelect value={controlPlace} onChange={setControlPlace} />
          </div>

          <div className="subtitle" style={{ marginTop: 20 }}>
            Data keluarga — cukup diisi sekali, bisa diubah nanti lewat menu
            "Data keluarga".
          </div>
          <SocioEconomicFields
            value={socio}
            onChange={setSocio}
            disabled={loading}
            idPrefix="onboarding"
          />

          {error && (
            <div className="subtitle" style={{ color: '#b91c1c', marginTop: 16 }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 24 }}>
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? 'Menyimpan...' : 'Simpan Profil'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfile;
